import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Clock, MailWarning } from 'lucide-react';
import ActionPreview, { hasPreview } from './ActionPreview';

// Carte d'accord de Nexus (revue le 2026-10-09 après « ce qu'on accepte n'est pas clair ; le détail technique est
// inutile ») : une phrase sur ce que Nexus va faire, ce qui en découle (lisible, sans JSON), l'aperçu de l'action quand
// Deepflow sait le montrer (le créneau proposé dans l'agenda du jour), un bouton qui dit ce qu'il fait (« Bloquer et
// inviter 2 personnes »), et jusqu'à quand Nexus attend la réponse. Depuis : le même aperçu du résultat pour les
// heures, tâches, objectifs, sprints, points, notes et messages (ActionPreview).

const SummaryList = ({ items = [] }) => {
  if (!items || items.length === 0) {
    return <p className="text-sm text-slate-500">Vérifie et confirme pour poursuivre.</p>;
  }
  return (
    <ul className="space-y-1 text-sm text-slate-700">
      {items.map((item, index) => (
        <li key={index} className={`flex items-start gap-2 ${index === 0 ? 'font-medium text-slate-900' : ''}`}>
          <span className="mt-[0.45rem] h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-400" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
};

SummaryList.propTypes = {
  items: PropTypes.arrayOf(PropTypes.string),
};

const buildSummary = (tool, args) => {
  const summary = [];
  switch (tool) {
    case 'tasks__update':
      if (args.task_id) summary.push(`Mettre à jour la tâche #${args.task_id}`);
      if (Object.prototype.hasOwnProperty.call(args, 'is_completed')) {
        summary.push(args.is_completed ? 'La marquer comme terminée' : 'La marquer comme non terminée');
      }
      if (args.owner_name) summary.push(`Assigner à ${args.owner_name}`);
      if (args.due_date) summary.push(`Nouvelle échéance au ${args.due_date}`);
      if (args.priority) summary.push(`Priorité : ${args.priority}`);
      break;
    case 'tasks__create':
      summary.push(`Créer une nouvelle tâche "${args.name}"`);
      if (args.owner_name) summary.push(`Assignée à ${args.owner_name}`);
      if (args.due_date) summary.push(`Échéance fixée au ${args.due_date}`);
      break;
    case 'objectives__update_status':
      if (args.objective_id) summary.push(`Mettre à jour l’objectif #${args.objective_id}`);
      if (args.status) summary.push(`Nouveau statut : ${args.status}`);
      break;
    case 'objectives__create':
      summary.push(`Créer un nouvel objectif "${args.name}"`);
      if (args.owner_name) summary.push(`Responsable : ${args.owner_name}`);
      break;
    default:
      break;
  }
  return summary;
};

/** Jusqu'à quand Nexus attend la réponse (Hermes abandonne l'action ensuite). */
function useExpiry(expiresAt) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!expiresAt) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 20000);
    return () => clearInterval(timer);
  }, [expiresAt]);
  if (!expiresAt) return { expired: false, until: null };
  const end = new Date(expiresAt).getTime();
  return { expired: now >= end, until: new Date(end).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) };
}

const ConfirmationPrompt = ({ prompt, onConfirm, onCancel, disabled = false }) => {
  const actions = prompt.actions || [];
  const hasMultipleActions = actions.length > 1;
  const summaryItems = prompt.summary && prompt.summary.length > 0 ? prompt.summary : buildSummary(prompt.tool, prompt.arguments || {});
  const { expired, until } = useExpiry(prompt.expiresAt);
  const preview = hasPreview(prompt.preview) ? prompt.preview : null;
  const invites = (preview?.kind === 'agenda' && preview.invites) || [];
  const outward = invites.length > 0 || preview?.kind === 'message'; // des personnes seront prévenues
  // L'aperçu montre le détail : la phrase suffit au-dessus (l'agenda garde ses points rattachés) ; les invitations
  // sont dites dans l'encadré ci-dessous, pas deux fois.
  const shownSummary = preview && preview.kind !== 'agenda' ? summaryItems.slice(0, 1)
    : summaryItems.filter((line) => !(invites.length && line.startsWith('Google enverra')));

  const renderActionSummary = () => {
    if (!hasMultipleActions) return <SummaryList items={shownSummary} />;
    return (
      <div className="space-y-3">
        {actions.map((action, index) => {
          const actionSummary = (action.summary && action.summary.length > 0
            ? action.summary : buildSummary(action.tool, action.rawArguments || action.arguments || {})) || [];
          return (
            <div key={action.callId || action.title || `${action.tool}-${index}`} className="rounded-xl border border-indigo-100/70 bg-white/70 px-3 py-3">
              <p className="text-xs font-semibold uppercase text-indigo-500">Action {index + 1}{action.title ? ` · ${action.title}` : ''}</p>
              <div className="mt-2"><SummaryList items={actionSummary} /></div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="flex-1 rounded-2xl border border-indigo-200 bg-gradient-to-br from-white via-white to-indigo-50/60 p-4 shadow-md">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">Nexus attend ton accord</p>
          <h4 className="mt-1 text-sm font-bold text-slate-900">{prompt.title || prompt.tool}</h4>
        </div>
        {until && (
          <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${expired ? 'bg-slate-100 text-slate-500' : 'bg-indigo-100 text-indigo-600'}`}>
            <Clock className="h-3.5 w-3.5" /> {expired ? 'Expiré' : `Jusqu’à ${until}`}
          </span>
        )}
      </div>

      <div className="mt-3 space-y-3 rounded-xl bg-white/80 px-4 py-3">
        {renderActionSummary()}
        {preview && <ActionPreview preview={preview} />}
        {preview?.kind === 'message' && (
          <div className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            <MailWarning className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <span><strong>En acceptant, ce message part tout de suite</strong> sur Slack, à {preview.to}.</span>
          </div>
        )}
        {invites.length > 0 && (
          <div className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            <MailWarning className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <span>
              <strong>En acceptant, Google enverra une invitation</strong> à {invites.length > 1 ? 'ces personnes' : 'cette personne'} :{' '}
              {invites.join(', ')}.
            </span>
          </div>
        )}
      </div>

      {expired ? (
        <p className="mt-3 text-xs text-slate-500">Nexus n’attend plus : l’action n’a pas été faite. Redemande-la si tu la veux toujours.</p>
      ) : prompt.message && <p className="mt-3 text-xs text-slate-500">{prompt.message}</p>}

      {!prompt.answered && <div className="mt-4 flex flex-wrap gap-2">
        <button type="button" onClick={onConfirm} disabled={disabled || prompt.locked || expired}
          className={`inline-flex items-center justify-center rounded-full px-4 py-2 text-sm font-semibold text-white shadow transition disabled:cursor-not-allowed disabled:opacity-60 ${outward ? 'bg-amber-600 hover:bg-amber-700' : 'bg-indigo-600 hover:bg-indigo-700'}`}>
          {prompt.locked ? 'Action en cours…' : prompt.confirmLabel || 'Oui, confirmer'}
        </button>
        <button type="button" onClick={onCancel} disabled={disabled || prompt.locked}
          className="inline-flex items-center justify-center rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60">
          {expired ? 'Fermer' : 'Annuler'}
        </button>
      </div>}
    </div>
  );
};

ConfirmationPrompt.propTypes = {
  prompt: PropTypes.shape({
    tool: PropTypes.string.isRequired,
    arguments: PropTypes.object,
    callId: PropTypes.string,
    batchId: PropTypes.string,
    message: PropTypes.string,
    summary: PropTypes.arrayOf(PropTypes.string),
    title: PropTypes.string,
    locked: PropTypes.bool,
    answered: PropTypes.bool, // la réponse de Nexus est arrivée : plus de boutons
    preview: PropTypes.object,
    confirmLabel: PropTypes.string,
    expiresAt: PropTypes.string,
    actions: PropTypes.arrayOf(
      PropTypes.shape({
        tool: PropTypes.string,
        title: PropTypes.string,
        summary: PropTypes.arrayOf(PropTypes.string),
        callId: PropTypes.string,
        rawArguments: PropTypes.object,
        arguments: PropTypes.object,
      })
    ),
  }).isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default ConfirmationPrompt;
